<template>
	<div class="main">
		<div v-if="!isLoading">
			<div v-if="checkAllowance">
				<div class="text-left" v-if="$store.getters.isProcessLiability && isUserAllowedInLevel5or6 && repairExtensions && repairExtensions.length">
					<div>
						{{ $t('multiple_repair_extensions') }}
					</div>
					<div>
						{{ $t('approval_symbol') }}
						<span class="text-green-300 pl-2"><font-awesome-icon :icon="['fas', 'thumbs-up']"></font-awesome-icon></span>
					</div>
					<div>
						{{ $t('file_name_included_extension') }}
					</div>
				</div>

				<div class="text-left" v-else-if="isUserAllowedInLevel2">
					{{ $t('repair_extension_symbol_explanation_part_one')
					}}<span class="text-green-300"><font-awesome-icon :icon="['fas', 'thumbs-up']"></font-awesome-icon></span
					>{{ $t('repair_extension_symbol_explanation_part_two') }}
				</div>

				<div :key="downloadsCounter" v-if="repairExtensions && repairExtensions.length">
					<BaseButton
						class="mt-4"
						@click="newRepairExtension"
						v-if="isAppContainer && isUserAllowedInLevel5or6 && !isNewUnsavedExtension && repairExtensions.length > 0"
						>{{ $t('new_repair_extension') }}</BaseButton
					>
					<div
						v-for="(repairExtension, index) in repairExtensions"
						:key="repairExtension.id"
						class="bg-gray-100 shadow-md mb-8 my-6 rounded border-gray-300 border pb-4"
					>
						<div class="flex justify-between text-left mt-6 ml-8" v-if="isUserAllowedInLevel5or6">
							<div class="flex gap-6 w-full">
								<BaseButton
									v-if="repairExtension.pdf != null"
									:class="repairExtension.price ? 'mt-8 h-10' : ''"
									@click="getPDFURL(repairExtensions[index])"
									>{{ $t('download_repair_extension_pdf', { index: repairExtensions.length - index }) }}</BaseButton
								>
								<div class="w-1/4" v-if="repairExtension.price && repairExtension.pdf_created_at != null">
									<BaseInputNumber
										:disabled="true"
										v-model="repairExtension.price"
										:precision="2"
										:label="$t('price_repair_extension')"
										:required="true"
									/>
								</div>
								<BaseButton v-if="repairExtension.zip != null" @click="getZIPURL(repairExtensions[index])">{{
									$t('download_repair_extension_zip', { index: repairExtensions.length - index })
								}}</BaseButton>
							</div>
						</div>
						<RepairExtension
							ref="RepairExtension"
							:index="index"
							:real-index="repairExtensions.length - index"
							:repair-extension="repairExtension"
							:pdf_uploaded="changed.pdf"
							:zip_uploaded="changed.zip"
							v-if="$store.getters.isProcessLiability"
							@saveExtension="saveExtension"
							@scrollUp="$emit('scrollUp')"
							@statusSet="statusSet"
							@updateDocumentPdf="setDocumentUploaded(true, index)"
							@updateDocumentZip="setDocumentUploaded(false, index)"
						/>
						<OrderDocumentationRepairExtension
							ref="RepairExtension"
							:index="index"
							:real-index="repairExtensions.length - index"
							:repair-extension="repairExtension"
							:pdf_uploaded="changed.pdf"
							:zip_uploaded="changed.zip"
							v-else
							@saveExtension="saveExtension"
							@scrollUp="$emit('scrollUp')"
							@statusSet="statusSet"
							@updateDocumentPdf="setDocumentUploaded(true, index)"
							@updateDocumentZip="setDocumentUploaded(false, index)"
						/>
					</div>
				</div>

				<div class="text-left" v-else>
					<div class="mb-5">{{ $t('no_repair_extensions_found') }}</div>
					<div v-if="!isAppContainer">{{ $t('repair_extensions_tablet_hint') }}</div>
				</div>
			</div>
			<div v-else-if="$store.getters.isProcessOrderDocumentation || $store.getters.isProcessComprehensive">
				{{ $t('customer_app_not_installed') }}
			</div>
		</div>
		<LoadingSpinner v-else />
	</div>
</template>

<script>
import apiDefaults from '@/api/defaultConfiguration';
import { CustomersApiFactory, DamagecasesApiFactory } from '@/api/generated';
import { getUnixTimestamp } from '@/plugins/Utils';
import { v4 } from 'uuid';
import RepairExtension from '@/components/damagecases/forms/RepairExtension';
import BaseButton from '@/components/forms/buttons/BaseButton';
import { MetaInfoFileTypeEnum, RepairExtensionStatusEnum } from '@/api/generated/models';
import Authentication from '@/mixins/Authentication';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';
import OrderDocumentationRepairExtension from '@/components/damagecases/forms/OrderDocumentationRepairExtension.vue';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber.vue';

export default {
	name: 'RepairExtensions',
	components: { BaseInputNumber, OrderDocumentationRepairExtension, LoadingSpinner, BaseButton, RepairExtension },
	mixins: [Authentication, AppContainerMixin],
	mounted() {
		this.isLoading = true;
		this.$store.dispatch('updateDamageCase', { id: this.$route.params.processId || this.$route.params.id });
		this.fetchCustomer();
	},
	data() {
		return {
			repairExtensions: [],
			downloadsCounter: 0,
			isNewUnsavedExtension: false,
			documentPdf: { type: 'reparaturerweiterung_pdf' },
			documentZip: { type: 'reparaturerweiterung_zip' },
			changed: {
				status: false,
				zip: {
					uploaded: false,
					index: -1,
				},
				pdf: { uploaded: false, index: -1 },
			},
			isLoading: true,
			hasCustomerAppConnection: true,
		};
	},
	computed: {
		checkAllowance() {
			if (this.$store.getters.isProcessOrderDocumentation || this.$store.getters.isProcessComprehensive) {
				return this.hasCustomerAppConnection;
			} else {
				return true;
			}
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		areAllRepairExtensionApproved() {
			let isApproved = true;
			this.repairExtensions.forEach(repairextension => {
				if (repairextension.status === RepairExtensionStatusEnum.Requested) {
					isApproved = false;
				}
			});
			return isApproved;
		},
	},
	methods: {
		fetchCustomer() {
			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customersIdGet(this.$route.params.customer)
				.then(response => {
					this.hasCustomerAppConnection = response.data.customer_main != null;
				})
				.finally(_ => {
					this.fetchRepairExtensions();
				});
		},
		fetchRepairExtensions() {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairextensionsGet(parseInt(this.$route.params.processId || this.$route.params.id))
				.then(result => {
					this.repairExtensions = result.data;
					this.documentPdf.uuid = this.repairExtensions[0]?.pdf;
					this.documentZip.uuid = this.repairExtensions[0]?.zip;
					if (this.repairExtensions.length) {
						this.repairExtensions.forEach(repairExtension => {
							this.loadFiles(repairExtension);
						});
					}
				})
				.finally(_ => {
					this.isLoading = false;
				})
				.catch(error => {
					this.isLoading = false;
					const statusCode = error.response.status;
					if (
						statusCode === 404 &&
						!this.isUserAllowedInLevel1 &&
						!this.isUserAllowedInLevel2 &&
						((this.isUserAllowedInLevel5or6 && this.isAppContainer) || !this.isUserAllowedInLevel5or6)
					) {
						this.newRepairExtension();
					}
				});
		},
		setDocumentUploaded(isPdf, index) {
			if (isPdf) {
				this.changed.pdf.uploaded = true;
				this.changed.pdf.index = index;
			} else {
				this.changed.zip.uploaded = true;
				this.changed.zip.index = index;
			}
			this.fetchRepairExtensions();
		},
		// showCalcButton(index) {
		// 	return (
		// 		this.getAuthenticatedUserGroupLevel === '2' &&
		// 		this.repairExtensions[index].status === RepairExtensionStatusEnum.Approved &&
		// 		this.repairExtensions &&
		// 		this.repairExtensions.length
		// 	);
		// },
		statusSet(status) {
			this.changed.status = true;
			if (status === 'approved') {
				if (this.areAllRepairExtensionApproved) {
					this.$swal(this.$t('general.hint').toString(), this.$t('approve_repair_extension_upload_calc_file').toString(), 'warning');
					this.$emit('scrollUp');
				} else {
					this.$swal('', this.$t('approve_repair_extension_upload_calc_file').toString(), 'success');
				}
			} else {
				if (this.areAllRepairExtensionApproved) {
					this.$swal('', this.$t('denied_repair_extension_upload_calc_file').toString(), 'success');
					this.$emit('scrollUp');
				}
				this.changed.zip.uploaded = true;
				this.$swal('', this.$t('denied_repair_extension').toString(), 'success');
			}
		},
		setLeaveMessage() {
			if (!this.changed.zip.uploaded && !this.changed.pdf.uploaded && this.changed.status) {
				this.$store.commit('setConfirmLeaving', this.$t('file_missing_leave'));
			} else {
				this.$store.commit('setConfirmLeaving', false);
			}
		},
		updateDocumentPdf($event) {
			this.documentPdf = $event;
		},
		updateDocumentZip($event) {
			this.documentZip = $event;
		},
		getPDFURL(repairExtension) {
			const uuid = repairExtension.pdf;
			if (uuid != null) {
				new PDFDamagecasesRepository().getSavedDocument(uuid).then(response => {
					const label = response.headers['content-disposition'].split('filename=')[1];
					const blob = new Blob([response.data], { type: 'application/pdf' });
					const url = window.URL.createObjectURL(blob);
					setTimeout(function() {
						const a = document.createElement('a');
						a.style.display = 'none';
						a.href = url;
						a.download = label;
						document.body.appendChild(a);
						a.click();
						window.URL.revokeObjectURL(url);
					}, 200);
				});
			}
		},
		getZIPURL(repairExtension) {
			const uuid = repairExtension.zip;
			if (uuid != null) {
				if (this.isAppContainer) {
					this.$swal(this.$t('general.attention').toString(), this.$t('tablet_no_zip_files').toString(), 'warning');
				} else {
					new PDFDamagecasesRepository().getSavedDocument(uuid).then(response => {
						const label = response.headers['content-disposition'].split('filename=')[1];
						const blob = new Blob([response.data]);
						const url = window.URL.createObjectURL(blob);
						setTimeout(function() {
							const a = document.createElement('a');
							a.style.display = 'none';
							a.href = url;
							a.download = label;
							document.body.appendChild(a);
							a.click();
							window.URL.revokeObjectURL(url);
						}, 200);
					});
				}
			}
		},
		saveExtension(isNew) {
			this.isNewUnsavedExtension = false;
			if (!isNew) {
				this.$refs.RepairExtension.forEach(e => e.save(false));
			}
		},
		newRepairExtension() {
			if (this.$store.getters.getDamageCase.damageAssessor === undefined && this.$store.getters.isProcessLiability) {
				this.$swal(this.$t('general.attention').toString(), this.$t('pleaser_enter_damage_assessor').toString(), 'warning').then(() => {
					if (this.repairExtensions.length < 1) {
						this.$router.go(-1);
					}
				});
			} else {
				this.repairExtensions.unshift({
					id: v4(),
					imageFiles: [],
					description: '',
					damageAssessorComment: '',
					date: getUnixTimestamp(),
					price: null,
					isNotUploaded: true,
					status: RepairExtensionStatusEnum.Requested,
					isNew: true,
				});

				this.isNewUnsavedExtension = true;
			}
		},
		loadFiles(repairExtension) {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdRepairextensionsExtidMediafilesGet(parseInt(this.$route.params.processId || this.$route.params.id), repairExtension.id)
				.then(result => {
					const imageFiles = [];
					const data = result.data.reverse();
					data.forEach(file => {
						if (file.type === MetaInfoFileTypeEnum.Image) {
							imageFiles.push(file);
						}
					});
					repairExtension.imageFiles = imageFiles;
					this.downloadsCounter++;
				})
				.catch(error => {});
		},
	},
	watch: {
		changed: {
			deep: true,
			handler() {
				this.setLeaveMessage();
			},
		},
	},
};
</script>
